<template>
  <div>
    <!-- The wrapped content (section) -->
    <div ref="sectionRef" :id="sectionId">
      <slot />
    </div>

    <!-- Floating scroll button - teleported to body to escape DOM constraints -->
    <Responsive v-slot="{ breakpoint }">
      <Teleport v-if="breakpoint === 'wide'" to="body">
        <Transition name="fade">
          <FloatingScrollButton
            v-if="!isSectionVisible"
            :text="buttonText"
            :target-id="sectionId"
          />
        </Transition>
      </Teleport>
    </Responsive>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import FloatingScrollButton from './FloatingScrollButton.vue'

const props = defineProps({
  sectionId: {
    type: String,
    required: true
  },
  buttonText: {
    type: String,
    default: 'Scroll to section'
  }
})

const sectionRef = ref(null)
const isSectionVisible = ref(true)

let observer = null

const setupIntersectionObserver = () => {
  if (!sectionRef.value) return

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isSectionVisible.value = entry.isIntersecting
      })
    },
    {
      threshold: 0.1, // Section is considered visible when 10% of it is in viewport
      rootMargin: '0px 0px -50px 0px' // Slightly offset to hide button earlier
    }
  )

  observer.observe(sectionRef.value)
}

onMounted(() => {
  setupIntersectionObserver()
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
/* No additional styles needed - wrapper is transparent */

/* Fade transition for floating button */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
